import { AfterViewInit, ChangeDetectorRef, Component, ElementRef, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild, ViewEncapsulation } from '@angular/core';
import { AbstractControl, FormControl, FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { OneWebComponentsAngularModule } from '@one/angular';
import { Validators, Editor, Toolbar, schema, toDoc, toHTML, NgxEditorModule } from 'ngx-editor';
import { TextSelection } from 'prosemirror-state';

@Component({
  selector: 'dl-rich-text-document',
   standalone: true,
    imports:[OneWebComponentsAngularModule, NgxEditorModule,FormsModule,ReactiveFormsModule],
  templateUrl: './rich-text-document.component.html',
  styleUrl: './rich-text-document.component.scss',
  encapsulation: ViewEncapsulation.None
})
export class RichTextDocumentComponent implements OnInit, AfterViewInit, OnDestroy {

  public editor: Editor;
  public toolbar: Toolbar = [['bold', 'italic', 'underline'], ['ordered_list', 'bullet_list'], ['link']];
  public jsonDoc;
  public keyupFlagstatus = false;
  private _editorReady = false;
  private _lastEmittedHTML: string = '';
  private _mousedownPos: { x: number; y: number } | null = null;
  private _keydownHandler: ((e: KeyboardEvent) => void) | null = null;
  private _keyupHandler: ((e: KeyboardEvent) => void) | null = null;

  private _jsonTextItem: any = '';
  public _showInEditorItem = '';

  @ViewChild('editorRef', { read: ElementRef }) editorElRef: ElementRef;

  constructor(private cdr: ChangeDetectorRef, private elementRef: ElementRef) {}

  @Input()
  public get jsonTextItem(): any {
    return this._jsonTextItem;
  }
  public set jsonTextItem(jsonTextItemP: any) {
    const newValue = jsonTextItemP ?? '';
    
    if (this._editorReady && newValue === this._lastEmittedHTML) {
      this._jsonTextItem = newValue;
      return;
    }
    
    this._jsonTextItem = newValue;
    if (this._editorReady) {
      this.getHTMLTextFromJSON();
    }
  }

  @Input()
  public get showInEditor(): string {
    return this._showInEditorItem;
  }
  public set showInEditor(edP: string) {
    this._showInEditorItem = edP;
    if (this._editorReady) {
      this.getHTMLTextFromJSON();
    }
  }

  @Output() public jsonTextReturn = new EventEmitter<any>();

  public edform = new FormGroup({
    editorContent: new FormControl({ value: '', disabled: false }, Validators.required())
  });

  public get doc(): AbstractControl {
    return this.edform.get('editorContent');
  }

  public ngOnInit(): void {
    this.editor = new Editor();
    this.edform.get('editorContent').valueChanges.subscribe(chValue => {
      if (typeof chValue === 'string') {
        this.jsonDoc = toDoc(chValue, schema);
      } else {
        this.jsonDoc = chValue;
      }
      try {
        this._lastEmittedHTML = typeof chValue === 'string' ? chValue : toHTML(chValue, schema);
      } catch {
        this._lastEmittedHTML = '';
      }
      this.getUpdatedText();
    });

    this._editorReady = true;
    if (this._jsonTextItem) {
      this.getHTMLTextFromJSON();
    }
  }

  public ngAfterViewInit(): void {
    this.cdr.detectChanges();
    
    const editorElement = this.elementRef.nativeElement;
    
    this._keydownHandler = (event: KeyboardEvent) => {
      const proseMirror = editorElement.querySelector('.ProseMirror');
      if (!proseMirror || !proseMirror.contains(event.target as Node)) {
        return;
      }
      
      if (event.key === 'Enter' && !event.shiftKey && this.editor?.view) {
        event.stopImmediatePropagation();
        event.preventDefault();
        const view = this.editor.view;
        const { state } = view;
        const { $from, $to } = state.selection;
        
        let inListItem = false;
        let listItemDepth = -1;
        for (let d = $from.depth; d > 0; d--) {
          if ($from.node(d).type.name === 'list_item') {
            inListItem = true;
            listItemDepth = d;
            break;
          }
        }
        
        if (inListItem && listItemDepth > 0) {
          const tr = state.tr.split($from.pos, 2);
          view.dispatch(tr);
        } else {
          const tr = state.tr.split($from.pos);
          view.dispatch(tr);
        }
        return;
      }
      
      if (event.key === ' ' && this.editor?.view) {
        event.stopImmediatePropagation();
        event.preventDefault();
        const view = this.editor.view;
        const { state } = view;
        const { $from } = state.selection;
        const textBefore = $from.parent.textBetween(0, $from.parentOffset, null, '\ufffc');
        const lastChar = textBefore ? textBefore[textBefore.length - 1] : '';
        if (!textBefore || /^\s*$/.test(textBefore) || lastChar === ' ' || lastChar === '\u00A0') {
          const tr = state.tr.insertText('\u00A0');
          view.dispatch(tr);
        } else {
          const tr = state.tr.insertText(' ');
          view.dispatch(tr);
        }
        return;
      }
      
      event.stopPropagation();
    };
    
    this._keyupHandler = (event: KeyboardEvent) => {
      const proseMirror = editorElement.querySelector('.ProseMirror');
      if (proseMirror && proseMirror.contains(event.target as Node)) {
        event.stopImmediatePropagation();
      }
    };
    
    document.addEventListener('keydown', this._keydownHandler, true);
    document.addEventListener('keyup', this._keyupHandler, true);
  }

  public getUpdatedText(): void {
    this.jsonTextReturn.emit(this.jsonDoc);
  }

  public getHTMLTextFromJSON(): void {
    const value = this._jsonTextItem ?? '';
    this.edform.get('editorContent').setValue(value, { emitEvent: false });

    if (this.showInEditor === 'template') {
      this.edform.get('editorContent').disable();
    } else {
      this.edform.get('editorContent').enable();
    }
  }

  public editorkeydownEvent(evt) {
    this.keyupFlagstatus = true;
    this.getUpdatedText();
  }

  public onEditorMousedown(event: MouseEvent): void {
    const target = event.target as HTMLElement;
    if (target.closest('.NgxEditor__MenuBar')) {
      return;
    }
    
    this._mousedownPos = { x: event.clientX, y: event.clientY };
    
    if (this.editor?.view) {
      this.editor.view.focus();
    }
  }

  public onEditorMouseup(event: MouseEvent): void {
    const target = event.target as HTMLElement;
    if (target.closest('.NgxEditor__MenuBar')) {
      return;
    }
    
    if (this._mousedownPos) {
      const dragThreshold = 5;
      const dx = Math.abs(event.clientX - this._mousedownPos.x);
      const dy = Math.abs(event.clientY - this._mousedownPos.y);
      if (dx > dragThreshold || dy > dragThreshold) {
        this._mousedownPos = null;
        return;
      }
    }
    this._mousedownPos = null;
    
    if (event.detail > 1) {
      return;
    }
    
    if (this.editor?.view) {
      const view = this.editor.view;
      const coords = { left: event.clientX, top: event.clientY };
      const pos = view.posAtCoords(coords);
      
      if (pos) {
        const tr = view.state.tr.setSelection(TextSelection.create(view.state.doc, pos.pos));
        view.dispatch(tr);
      }
      
      view.focus();
    }
  }

  public ngOnDestroy(): void {
    if (this._keydownHandler) {
      document.removeEventListener('keydown', this._keydownHandler, true);
    }
    if (this._keyupHandler) {
      document.removeEventListener('keyup', this._keyupHandler, true);
    }
    this.editor.destroy();
  }
}

